import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { createTeam, TeamCreateSchema } from "@/queries/team";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { TeamForm, TeamFormValues, teamSchema } from "./TeamForm";

export function CreateTeamButton() {
  const [open, setOpen] = useState(false);
  const { mutate: createTeamMutation, isPending } = createTeam();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      name: "",
      description: "",
      leader_id: undefined,
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: TeamFormValues) => {
    const teamData: TeamCreateSchema = {
      name: data.name,
      description: data.description,
      leader_id: data.leader_id,
    };

    createTeamMutation(teamData, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Team created successfully.",
        });
        queryClient.invalidateQueries({ queryKey: ["teams"] });
        form.reset();
        setOpen(false);
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          title: "Error creating team",
          description: error.message,
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary" className="h-8 border-dashed">
          <Plus className="h-4 w-4 mr-2" />
          Create Team
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground" />
        </DialogHeader>
        <div className="py-4">
          <TeamForm
            form={form}
            onSubmit={onSubmit}
            isLoading={isPending}
            submitButtonText="Create Team"
            onClose={() => setOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
